import { Fragment, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Badge } from "../../../../ui/badge";
import { Button } from "../../../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../ui/table";
import { Textarea } from "../../../../ui/textarea";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";

import { ScenarioHeader } from "../../../../components/portfolio-risk-concentration/ScenarioHeader";
import { ScenarioSpinner } from "../../../../components/portfolio-risk-concentration/ScenarioSpinner";
import { StatCard } from "../../../../components/portfolio-risk-concentration/StatCard";
import { usd } from "../../../../components/portfolio-risk-concentration/format";
import { hasTier } from "../../../../components/portfolio-risk-concentration/access-control";
import { cn } from "../../../../utils/common/cn";
import {
  conditionSummary,
  downloadScenarioPledgeProposal,
  apiDetail,
  useCreateScenarioPledgeProposals,
  useReviewScenarioPledgeProposal,
  useReviewScenarioRetireRequest,
  useScenarioPledgeProposals,
  useScenarioRetireRequests,
  type ScenarioPledgeDestinationInput,
  type ScenarioPledgeProposal,
  codesLabel,
} from "../../../../utils/portfolio-risk-concentration/api/scenario";
import type { ScenarioDraft } from "../ScenarioSimulatorPage";
import ScenarioReconciliationPanel from "./ScenarioReconciliationPanel";

const LOG_PAGE_SIZE = 10;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatDateRange(from: string, to: string): string {
  const parse = (s: string) => {
    const [y, m, d] = s.split("-").map(Number);
    return { y, m, d };
  };
  const f = parse(from);
  const t = parse(to);
  const fMonth = MONTHS[f.m - 1];
  const tMonth = MONTHS[t.m - 1];
  if (fMonth === tMonth && f.y === t.y) return `${fMonth} ${f.d}–${t.d}`;
  return `${fMonth} ${f.d} – ${tMonth} ${t.d}`;
}

interface DestinationDraft extends ScenarioPledgeDestinationInput {
  key: string;
  clauseBadges: string[];
}

/** One row per distinct destination the waterfall actually touched: several conditions can
 *  route to the same investor code + facility, so this groups by that pair rather than by
 *  condition index. clauseBadges pulls the real human-readable condition summary (shared with
 *  ScenarioApplyStep's own cards via conditionSummary()) rather than a placeholder string. */
function buildDestinations(draft: ScenarioDraft): DestinationDraft[] {
  const byKey = new Map<string, DestinationDraft>();
  for (const result of draft.waterfall?.conditions ?? []) {
    if (!result.destination || result.matchedKeys.length === 0) continue;
    const key = `${result.destination.investorCode}|${result.destination.facilityCode}`;
    const sourceCondition = draft.conditions[result.index];
    const badges = sourceCondition ? conditionSummary(sourceCondition, result.contractCount) : [];
    const existing = byKey.get(key);
    if (existing) {
      existing.contractKeys = [...existing.contractKeys, ...result.matchedKeys];
      existing.dollarTotal += result.dollarTotal;
      existing.contractCount += result.contractCount;
      existing.clauseBadges = [...existing.clauseBadges, ...badges];
    } else {
      byKey.set(key, {
        key,
        investorCode: result.destination.investorCode,
        facilityCode: result.destination.facilityCode,
        contractKeys: [...result.matchedKeys],
        dollarTotal: result.dollarTotal,
        contractCount: result.contractCount,
        matchedRuleSummary: `Scenario condition ${result.index + 1}`,
        reason: `Pledge ${result.contractCount} contract(s) from Investor ${codesLabel(draft.sourceInvestorCode)} to ${result.destination.investorCode} (${result.destination.facilityCode}) per this scenario.`,
        clauseBadges: badges,
      });
    }
  }
  return Array.from(byKey.values());
}

function submitLabel(count: number): string {
  if (count === 1) return "Submit for review";
  if (count === 2) return "Submit both for review";
  return "Submit all for review";
}

/** 5 · Propose scenario: one governed pledge record per destination, plus Log and Scenario
 *  Reconciliation as two tabs underneath. Finalizing here never touches the source system; it
 *  records a decision and a downloadable CSV someone applies manually. */
export default function ScenarioProposeStep({
  draft,
  onFinalized,
  base,
}: {
  draft: ScenarioDraft;
  onFinalized: () => void;
  base: string;
}) {
  const navigate = useNavigate();
  const create = useCreateScenarioPledgeProposals();
  // One reference per Propose screen, sent with every submit: if a submit times out after the records were saved and is clicked again,
  // the server answers with what it already created instead of raising the proposals twice.
  const scenarioRef = useRef(`scn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

  const initialDestinations = useMemo(() => buildDestinations(draft), [draft]);
  const [destinations, setDestinations] = useState(initialDestinations);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<string[] | null>(null);
  const [tab, setTab] = useState<"log" | "reconciliation">("log");

  const remainder = draft.waterfall?.remainder;

  function updateReason(key: string, reason: string) {
    setDestinations((rows) => rows.map((r) => (r.key === key ? { ...r, reason } : r)));
  }

  async function submitAll() {
    if (destinations.some((d) => !d.reason.trim())) {
      setError("Every destination needs a reason before it can be proposed.");
      return;
    }
    setError(null);
    try {
      const res = await create.mutateAsync({
        sourceInvestorCode: draft.sourceInvestorCode,
        scenarioRef: scenarioRef.current,
        // the scenario's own inputs, so it can be opened again once approved: how its pool was chosen, and the dates that mode uses. An
        // uploaded file is the pool, so it saves the file's contracts and the effective date and has no booking window; a scenario built
        // from investor codes saves its booking window and the effective date it was read on.
        sourceMode: draft.sourceKeys !== null ? "UPLOAD" : "MANUAL",
        effectiveDate: draft.poolFileDate ?? undefined,
        poolKeys: draft.sourceKeys ?? undefined,
        dateFrom: draft.sourceKeys !== null ? undefined : draft.dateFrom,
        dateTo: draft.sourceKeys !== null ? undefined : draft.dateTo,
        conditions: draft.conditions,
        fromUpload: draft.sourceKeys !== null,
        destinations: destinations.map(({ key: _key, clauseBadges: _badges, ...rest }) => rest),
      });
      setSubmitted(res.proposalIds);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submit failed.");
    }
  }

  return (
    <div>
      <ScenarioHeader
        title="Propose scenario"
        subtitle={
          destinations.length === 0
            ? "Nothing was assigned to a destination in this scenario."
            : `This scenario touched ${destinations.length} destination${destinations.length > 1 ? "s" : ""}. ` +
              `Confirming below creates ${destinations.length} independent proposal${destinations.length > 1 ? "s" : ""}, ` +
              "each enters its own review and approval."
        }
      />

      {submitted ? (
        <Card className="mt-4 border-[color:var(--approve)]/40">
          <CardContent className="py-4 text-sm">
            <p className="font-medium text-[color:var(--approve)]">
              {submitted.length} pledge proposal(s) submitted for review.
            </p>
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={onFinalized}>
                Start a new scenario
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {destinations.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">
              Go back to Scenario to route some contracts before proposing.
            </p>
          ) : (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {destinations.map((d, i) => (
                <Card key={d.key}>
                  <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
                    <div>
                      <CardTitle className="text-sm font-semibold">
                        Proposal {i + 1} · Code {d.investorCode} · {d.facilityCode}
                      </CardTitle>
                      {d.clauseBadges.length > 0 && (
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {d.clauseBadges.join(" · ")},{" "}
                          {draft.sourceKeys !== null
                            ? `uploaded contracts, effective ${draft.poolFileDate ?? draft.asOfDate}`
                            : `booked ${formatDateRange(draft.dateFrom, draft.dateTo)}`}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className="border-amber/50 text-[10px] uppercase tracking-[0.1em] text-amber">
                      Draft
                    </Badge>
                  </CardHeader>
                  <CardContent>
                    <p className="text-lg font-semibold text-amber">{usd(d.dollarTotal)}</p>
                    <p className="text-[11px] text-muted-foreground">{d.contractCount} contracts</p>

                    <p className="mt-3 text-[11px] font-medium text-muted-foreground">Reason</p>
                    <Textarea
                      className="mt-1"
                      rows={2}
                      value={d.reason}
                      onChange={(e) => updateReason(d.key, e.target.value)}
                    />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {remainder && remainder.contractCount > 0 && (
            <div className="mt-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              {usd(remainder.dollarTotal)} across {remainder.contractCount} contract(s) remains in
              Investor {codesLabel(draft.sourceInvestorCode)}, not part of {destinations.length > 1 ? "either" : "this"}{" "}
              proposal.
            </div>
          )}

          {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

          <div className="mt-4 flex justify-between">
            <Button variant="outline" onClick={() => navigate(`${base}/snapshot`)}>
              ← Back
            </Button>
            <Button disabled={destinations.length === 0 || create.isPending} onClick={submitAll}>
              {create.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {create.isPending ? "Submitting…" : submitLabel(destinations.length)}
            </Button>
          </div>
        </>
      )}

      <div className="mt-6 flex gap-2 border-b border-border pb-2">
        <Button
          size="sm"
          variant={tab === "log" ? "default" : "outline"}
          onClick={() => setTab("log")}
        >
          Log
        </Button>
        <Button
          size="sm"
          variant={tab === "reconciliation" ? "default" : "outline"}
          onClick={() => setTab("reconciliation")}
        >
          Scenario Reconciliation
        </Button>
      </div>

      <div className="mt-4">
        {tab === "log" ? (
          <div className="space-y-6">
            <ScenarioProposalLog />
            <ScenarioRetireLog />
          </div>
        ) : (
          <ScenarioReconciliationPanel />
        )}
      </div>
    </div>
  );
}

const STATUS_STYLE: Record<string, string> = {
  PENDING: "border-primary/50 text-primary",
  APPROVED: "border-amber/50 text-amber",
  EXECUTED_CONFIRMED: "border-[color:var(--approve)]/50 text-[color:var(--approve)]",
  DISCREPANCY: "border-destructive/50 text-destructive",
  REJECTED: "border-destructive/50 text-destructive",
};

function statusLabel(status: string): string {
  return status.replace(/_/g, " · ");
}

/** Every proposal ever submitted from this workflow: nothing here is ever deleted, and more
 *  than one can sit pending at once. A reviewer's own action is only Approve/Reject --
 *  EXECUTED_CONFIRMED/DISCREPANCY are derived automatically from real reconciliation, not
 *  chosen here (see scenario.py's module docstring). */
function ScenarioProposalLog() {
  const [offset, setOffset] = useState(0);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const logQ = useScenarioPledgeProposals({ limit: LOG_PAGE_SIZE, offset });
  const review = useReviewScenarioPledgeProposal();
  const canDecide = hasTier("contributor");

  const proposals = logQ.data?.proposals ?? [];
  const total = logQ.data?.total ?? 0;

  async function download(proposalId: string) {
    try {
      setDownloading(proposalId);
      await downloadScenarioPledgeProposal(proposalId);
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [proposalId]: err instanceof Error ? err.message : "Download failed.",
      }));
    } finally {
      setDownloading(null);
    }
  }

  async function act(proposalId: string, decision: "APPROVED" | "REJECTED") {
    const note = (notes[proposalId] ?? "").trim();
    if (!note) {
      setErrors((e) => ({ ...e, [proposalId]: "Review notes are required." }));
      return;
    }
    try {
      setBusy(proposalId);
      const res = await review.mutateAsync({ proposalId, decision, notes: note });
      if (!res.ok) throw new Error(res.error ?? "Review failed.");
      setNotes((n) => ({ ...n, [proposalId]: "" }));
      setErrors((e) => ({ ...e, [proposalId]: "" }));
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [proposalId]: err instanceof Error ? err.message : "Review failed.",
      }));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold">Log</CardTitle>
        <p className="text-xs text-muted-foreground">
          Every proposal ever submitted from this workflow: nothing here is ever deleted, and
          more than one can sit pending at once.
        </p>
      </CardHeader>
      <CardContent className="px-0">
        {logQ.isLoading ? (
          <ScenarioSpinner label="Loading log…" />
        ) : (
          <>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Status</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Facility</TableHead>
              <TableHead className="text-right">Financed Amount</TableHead>
              <TableHead className="text-right">Contracts</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Reviewed By</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {proposals.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="py-6 text-center text-sm text-muted-foreground">
                  No pledge proposals yet.
                </TableCell>
              </TableRow>
            )}
            {proposals.map((p) => {
              const canDownload = p.status !== "PENDING" && p.status !== "REJECTED";
              const isExpanded = !!expanded[p.proposalId];
              return (
                <Fragment key={p.proposalId}>
                <TableRow>
                  <TableCell>
                    <ExpandToggle
                      expanded={isExpanded}
                      label={`proposal ${p.proposalId}`}
                      onClick={() =>
                        setExpanded((e) => ({ ...e, [p.proposalId]: !e[p.proposalId] }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn("text-[10px] uppercase tracking-[0.1em]", STATUS_STYLE[p.status])}
                    >
                      {statusLabel(p.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px] uppercase tracking-[0.1em]",
                        p.scenarioStatus === "RETIRED"
                          ? "text-muted-foreground"
                          : "border-[color:var(--approve)]/50 text-[color:var(--approve)]",
                      )}
                    >
                      {p.scenarioStatus === "RETIRED" ? "Retired" : "Active"}
                    </Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs">
                    {codesLabel(p.sourceInvestorCode)} → {p.destinationInvestorCode}
                    {p.destinationFacilityCode ? ` (${p.destinationFacilityCode})` : ""}
                  </TableCell>
                  <TableCell className="text-right">{usd(p.dollarTotal)}</TableCell>
                  <TableCell className="text-right">{p.contractCount}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {p.proposedByRole} · {new Date(p.proposedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {p.reviewedByRole ?? "—"}
                  </TableCell>
                  <TableCell className="max-w-[220px] text-xs text-muted-foreground">
                    {p.reviewedByRole ? (
                      p.reconciliationNote ?? p.reviewNotes ?? "—"
                    ) : p.status === "PENDING" && canDecide ? (
                      <div className="space-y-1.5">
                        <Textarea
                          rows={1}
                          placeholder="Review notes (required)"
                          value={notes[p.proposalId] ?? ""}
                          onChange={(e) => setNotes((n) => ({ ...n, [p.proposalId]: e.target.value }))}
                          className="text-xs"
                        />
                        {errors[p.proposalId] && (
                          <p className="text-destructive">{errors[p.proposalId]}</p>
                        )}
                        <div className="flex flex-wrap gap-1">
                          <Button
                            size="sm"
                            className="h-6 px-2 text-[11px]"
                            disabled={busy === p.proposalId}
                            onClick={() => act(p.proposalId, "APPROVED")}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-6 px-2 text-[11px]"
                            disabled={busy === p.proposalId}
                            onClick={() => act(p.proposalId, "REJECTED")}
                          >
                            Reject
                          </Button>
                        </div>
                      </div>
                    ) : p.status === "PENDING" ? (
                      "Awaiting review"
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {canDownload ? (
                      <button
                        type="button"
                        disabled={downloading === p.proposalId}
                        onClick={() => download(p.proposalId)}
                        className="text-[11px] text-primary hover:underline disabled:opacity-50"
                      >
                        {downloading === p.proposalId ? "Downloading…" : "Download CSV"}
                      </button>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={10} className="bg-muted/20 p-0">
                      <ProposalExplainer proposal={p} />
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between px-4 pt-3 text-xs text-muted-foreground">
          <span>
            {total === 0 ? "0 of 0" : `${offset + 1}–${Math.min(offset + LOG_PAGE_SIZE, total)} of ${total}`}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - LOG_PAGE_SIZE))}
            >
              Prev
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              disabled={offset + LOG_PAGE_SIZE >= total}
              onClick={() => setOffset((o) => o + LOG_PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** Chevron toggle for a Log row -- same bordered-square button
 *  risk-pricing-engine's Manage Targets page uses for its Office/Industry
 *  drill-down rows, so this reads the same way. */
function ExpandToggle({ expanded, onClick, label }: { expanded: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      className="flex size-5 items-center justify-center border border-border text-muted-foreground hover:text-foreground"
      aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
      onClick={onClick}
    >
      {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
    </button>
  );
}

/** The Log row's own detail, revealed by its chevron -- the scenario a proposal came from,
 *  what was proposed, and (once decided) what was approved/rejected and when. An inline
 *  expanded row under the proposal (same drill-down pattern as risk-pricing-engine's Manage
 *  Targets Office/Industry rows), not a separate sidebar/sheet. */
function ProposalExplainer({ proposal: p }: { proposal: ScenarioPledgeProposal }) {
  return (
    <div className="space-y-3 px-4 py-4">
      <p className="text-xs text-muted-foreground">
        {p.reason}
        {p.matchedRuleSummary ? ` — ${p.matchedRuleSummary}.` : ""}
      </p>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Financed Amount" value={usd(p.dollarTotal)} />
        <StatCard label="Contracts" value={p.contractCount} />
        <StatCard
          label="Proposed"
          value={new Date(p.proposedAt).toLocaleDateString()}
          sublabel={p.proposedByRole}
        />
        <StatCard
          label={p.status === "REJECTED" ? "Rejected" : "Approved"}
          value={p.reviewedAt ? new Date(p.reviewedAt).toLocaleDateString() : "—"}
          sublabel={p.reviewedByRole ?? "Awaiting review"}
        />
      </div>

      <div className="rounded-md border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
        Moves {p.contractCount} contract{p.contractCount === 1 ? "" : "s"} ({usd(p.dollarTotal)}) from
        Investor {codesLabel(p.sourceInvestorCode)} to {p.destinationInvestorCode}
        {p.destinationFacilityCode ? ` (${p.destinationFacilityCode})` : ""}.
      </div>

      {(p.reconciliationNote || p.reviewNotes) && (
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            {p.reconciliationNote ? "Reconciliation" : "Review notes"}
          </p>
          <p className="mt-1 text-sm">{p.reconciliationNote ?? p.reviewNotes}</p>
        </div>
      )}
    </div>
  );
}

const RETIRE_STATUS_STYLE: Record<string, string> = {
  PENDING: "border-primary/50 text-primary",
  APPROVED: "border-[color:var(--approve)]/50 text-[color:var(--approve)]",
  REJECTED: "border-destructive/50 text-destructive",
};

/** Requests to retire an approved scenario: the queue a contributor decides, and the permanent log of every request and decision.
 *  Nothing is deleted -- approving marks the scenario Retired (State column above and on Scenario Portfolio); rejecting leaves it
 *  Active. The same proposer / approver shape as the pledge log above: a reviewer asks (the trash icon on a Reconciliation card), a
 *  contributor approves or rejects, notes are required. */
function ScenarioRetireLog() {
  const [offset, setOffset] = useState(0);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const logQ = useScenarioRetireRequests({ limit: LOG_PAGE_SIZE, offset });
  const review = useReviewScenarioRetireRequest();
  const canDecide = hasTier("contributor");

  const requests = logQ.data?.requests ?? [];
  const total = logQ.data?.total ?? 0;

  async function act(retireId: string, decision: "APPROVED" | "REJECTED") {
    const note = (notes[retireId] ?? "").trim();
    if (!note) {
      setErrors((e) => ({ ...e, [retireId]: "Review notes are required." }));
      return;
    }
    try {
      setBusy(retireId);
      const res = await review.mutateAsync({ retireId, decision, notes: note });
      if (!res.ok) throw new Error(res.error ?? "Review failed.");
      setNotes((n) => ({ ...n, [retireId]: "" }));
      setErrors((e) => ({ ...e, [retireId]: "" }));
    } catch (err) {
      setErrors((e) => ({ ...e, [retireId]: apiDetail(err, "Review failed.") }));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold">Retire requests</CardTitle>
        <p className="text-xs text-muted-foreground">
          Requests to retire an approved scenario, and every decision on them. Nothing is deleted: an approved request marks the scenario
          Retired, and a rejected one leaves it Active.
        </p>
      </CardHeader>
      <CardContent className="px-0">
        {logQ.isLoading ? (
          <ScenarioSpinner label="Loading retire requests…" />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Scenario</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Decided By</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                      No retire requests yet.
                    </TableCell>
                  </TableRow>
                )}
                {requests.map((r) => (
                  <TableRow key={r.retireId}>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn("text-[10px] uppercase tracking-[0.1em]", RETIRE_STATUS_STYLE[r.status])}
                      >
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs">{r.scenarioLabel ?? r.scenarioRef}</TableCell>
                    <TableCell className="max-w-[220px] text-xs text-muted-foreground">{r.reason}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {r.proposedByRole}
                      {r.proposedAt ? ` · ${new Date(r.proposedAt).toLocaleDateString()}` : ""}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {r.reviewedByRole
                        ? `${r.reviewedByRole}${r.reviewedAt ? ` · ${new Date(r.reviewedAt).toLocaleDateString()}` : ""}`
                        : "—"}
                    </TableCell>
                    <TableCell className="max-w-[240px] text-xs text-muted-foreground">
                      {r.reviewedByRole ? (
                        (r.reviewNotes ?? "—")
                      ) : r.status === "PENDING" && canDecide ? (
                        <div className="space-y-1.5">
                          <Textarea
                            rows={1}
                            placeholder="Review notes (required)"
                            value={notes[r.retireId] ?? ""}
                            onChange={(e) => setNotes((n) => ({ ...n, [r.retireId]: e.target.value }))}
                            className="text-xs"
                          />
                          {errors[r.retireId] && <p className="text-destructive">{errors[r.retireId]}</p>}
                          <div className="flex flex-wrap gap-1">
                            <Button
                              size="sm"
                              className="h-6 px-2 text-[11px]"
                              disabled={busy === r.retireId}
                              onClick={() => act(r.retireId, "APPROVED")}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="h-6 px-2 text-[11px]"
                              disabled={busy === r.retireId}
                              onClick={() => act(r.retireId, "REJECTED")}
                            >
                              Reject
                            </Button>
                          </div>
                        </div>
                      ) : (
                        "Awaiting review"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between px-4 pt-3 text-xs text-muted-foreground">
              <span>
                {total === 0 ? "0 of 0" : `${offset + 1}–${Math.min(offset + LOG_PAGE_SIZE, total)} of ${total}`}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  disabled={offset === 0}
                  onClick={() => setOffset((o) => Math.max(0, o - LOG_PAGE_SIZE))}
                >
                  Prev
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  disabled={offset + LOG_PAGE_SIZE >= total}
                  onClick={() => setOffset((o) => o + LOG_PAGE_SIZE)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
